// i18n/pluralRules.js
//
// Arabic plural selection for vue-i18n — registered as `pluralRules.ar` in
// `src/i18n/index.js`.
//
// WHY THIS EXISTS. vue-i18n ships no rule for Arabic. With none registered it uses
// its own default, `choice ? Math.min(choice, 2) : 0`, which can only ever reach
// the first THREE forms of a message. `box.totals.lineCount` and
// `box.totals.openCarton` are written with SIX forms, in the CLDR order —
//
//   zero | one | two | few | many | other
//
// — so on the default rule any count from 2 upward picked the third form, the DUAL:
// a carton of 3 lines read «منتجان» ("two products"), and so did one of 11. Only
// 0, 1 and 2 were ever right.
//
// THE FIX IS THE RULE, NOT THE COPY. Those strings are already correct for the six
// CLDR categories; the gap was that nothing told vue-i18n which count belongs in
// which. So this maps counts to the six categories and touches no Arabic text.
//
// WHAT IT DELIBERATELY DOES NOT DO. A rule registered for a locale replaces the
// default for EVERY message in that locale, so it only claims the one shape it
// exists for — a message with exactly six forms — and hands everything else back
// to vue-i18n's own rule (`orgRule`, which vue-i18n supplies whenever a custom rule
// is registered). The four 3-form strings (zero | one | other, e.g.
// `box.shipGate.onTheWay`) therefore behave exactly as they always did.
//
// THE GUARD: `tests/unit/i18n/arabic-plurals.test.js` fails if any Arabic plural
// message has a shape other than 2, 3 or 6 forms. A four- or five-form string would
// fall through to the default and misread most counts, so it has to be a conscious
// decision here rather than a report from a member.

// Indexes into [zero, one, two, few, many, other].
const ZERO = 0;
const ONE = 1;
const TWO = 2;
const FEW = 3;
const MANY = 4;
const OTHER = 5;

const SIX_FORMS = 6;

/**
 * vue-i18n's own default rule, copied for the one case where nobody passed it in
 * (a direct call). Inside vue-i18n `orgRule` is always supplied, so this is never
 * what a member's screen runs.
 */
function defaultRule(choice, choicesLength) {
  const count = Math.abs(choice);
  if (choicesLength === 2) return count ? (count > 1 ? 1 : 0) : 1;
  return count ? Math.min(count, 2) : 0;
}

/**
 * The CLDR plural category of a count in Arabic, as an index into the six forms.
 *
 *   0                     → zero
 *   1                     → one
 *   2                     → two   (exactly 2 — 102 is "other", not "two")
 *   n % 100 in 3..10      → few
 *   n % 100 in 11..99     → many
 *   everything else       → other (100, 101, 102, 200, 1000 …)
 *
 * Few and many are defined for whole numbers only, so a fraction is "other".
 */
function arabicCategory(choice) {
  // The magnitude, like the default rule: vue-i18n hands -1 in when the caller gave
  // no count at all, which reads as "one" — the same form the default picks.
  const count = Math.abs(choice);

  if (!Number.isInteger(count)) return OTHER;
  if (count === 0) return ZERO;
  if (count === 1) return ONE;
  if (count === 2) return TWO;

  const lastTwoDigits = count % 100;
  if (lastTwoDigits >= 3 && lastTwoDigits <= 10) return FEW;
  if (lastTwoDigits >= 11) return MANY;
  return OTHER;
}

/**
 * @param {number} choice        the count vue-i18n resolved from `{ count }`, `{ n }`
 *                               or a positional plural argument
 * @param {number} choicesLength how many `|`-separated forms the message has
 * @param {(choice: number, choicesLength: number) => number} [orgRule]
 *                               vue-i18n's default rule
 * @returns {number} the index of the form to render
 */
export function arabicPlural(choice, choicesLength, orgRule) {
  if (choicesLength === SIX_FORMS) return arabicCategory(choice);

  return (orgRule ?? defaultRule)(choice, choicesLength);
}
